import { useState } from 'react';
import { supabase } from '../lib/supabase/client';

interface ResumeServiceRequest {
  name: string;
  email: string;
  phone: string;
  experience_summary?: string;
}

export function useResumeService() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submitRequest = async (data: ResumeServiceRequest) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const { data: { user } } = await supabase.auth.getUser();

      const { error: insertError } = await supabase
        .from('resume_service_requests')
        .insert({
          user_id: user?.id || null,
          name: data.name,
          email: data.email,
          phone: data.phone,
          experience_summary: data.experience_summary,
          status: 'pending',
          payment_status: 'pending'
        });

      if (insertError) throw insertError;

      setSuccess(true);
      return true;
    } catch (err) {
      console.error('Error submitting resume service request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit request');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getUserRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Must be logged in to view requests');

      const { data, error: fetchError } = await supabase
        .from('resume_service_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      return data;
    } catch (err) {
      console.error('Error fetching resume service requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch requests');
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    submitRequest,
    getUserRequests,
    loading,
    error,
    success
  };
}
